import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, TouchableOpacity, Platform, Keyboard } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useSharedValue } from 'react-native-reanimated';
import { KeyboardChatScrollView, KeyboardStickyView } from 'react-native-keyboard-controller';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveComment, deleteComment, getBoardPermissions, getMutedPersonIDsForBoard, addPostReaction, removePostReaction, addCommentReaction, removeCommentReaction, recordPostView } from '@/data/connect';
import MessageInput from '@/components/Connect/Conversations/MessageInput';
import EditContainer from '@/components/Connect/Conversations/EditContainer';
import { createNotification } from '@/data/notifications';
import useMarkNotificationsRead from '@/utils/hooks/useMarkNotificationsRead';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { supabase } from '@/data/client';
import Avatar from '@/components/People/Avatar';
import { renderTextWithMentions } from '@/utils/mentionHelper';
import { LegendList } from "@legendapp/list";
import { useFocusEffect, useLocalSearchParams, useRouter, useIsFocused } from 'expo-router';
import Toast from 'react-native-toast-message'
import Unicon from '@/components/General/Unicon';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import RelativeTime from '@/components/General/RelativeTime';
import Post from '@/components/Connect/Boards/Post';
import Chip from '@/components/General/Chip';
import { saveToRecents } from '@/utils/recentlyVisited';
import usePlatform from '@/utils/hooks/usePlatform';
import AdminViewingBanner from '@/components/General/AdminViewingBanner';
import useMessagesStore from '@/data/store/useMessagesStore';
import { ConnectedReactionMenu } from '@/components/Connect/ReactionMenu';
import Reaction from '@/components/Connect/Reaction';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import ModalWrapper from '@/components/General/ModalWrapper';
import ModalHeader from '@/components/General/ModalHeader';
import { showAlert } from '@/utils/showAlert';
import Clipboard from '@react-native-clipboard/clipboard';
import { impactLight } from '@/utils/haptic';
import MediaViewer from '@/components/General/MediaViewer';
import { getFile, getMediaFileInfo } from '@/utils/FileManager';

//Chat-style scroll view: keeps content above the sticky MessageInput and lets the list
//scroll up when the keyboard opens (KeyboardAwareScrollView only tracks inputs *inside*
//it, but MessageInput lives outside in KeyboardStickyView — see conversations screen).
const ChatScrollView = (props, ref) => (
    <KeyboardChatScrollView
        ref={ref}
        automaticallyAdjustContentInsets={false}
        contentInsetAdjustmentBehavior="never"
        keyboardDismissMode="interactive"
        {...props}
    />
);

//Single comment row. Owns its reactionRef (ReactionMenu trigger anchor) and the
//grouped-reactions display state — grouping logic mirrors Post.jsx/ChatBubble.jsx.
const CommentItem = ({comment, isLast, isPostAuthor, canReact, canEdit, canDelete, onToggleReaction, onEditComment, onDeleteComment}) => {
    const router = useRouter();
    const reactionRef = useRef();
    const setMessageForReaction = useMessagesStore(state => state.setMessageForReaction);
    const [reactions, setReactions] = useState({});
    const [attachmentMedia, setAttachmentMedia] = useState([]);

    useEffect(() => {
        if(comment?.Reactions && comment.Reactions.length > 0){
            const reactionTypes = {}
            comment.Reactions.forEach((r) => {
                if(!reactionTypes[r.reaction]){
                    reactionTypes[r.reaction] = []
                }

                reactionTypes[r.reaction].push(r)
            })

            setReactions(reactionTypes)
        }
        else{
            setReactions({})
        }
    }, [JSON.stringify(comment?.Reactions)])

    //Load attachment media (mirrors Post.jsx / ChatBubble.jsx): resolve storage keys
    //to playable URIs via getFile, then getMediaFileInfo for type/dimensions.
    useEffect(() => {
        if(!comment?.attachments || comment.attachments.length === 0){
            setAttachmentMedia([]);
            return;
        }

        let cancelled = false;
        const promises = comment.attachments.map(key =>
            key?.startsWith('http') ? {uri: key} : getFile(key, true, true)
        );

        Promise.all(promises).then(async (results) => {
            if(cancelled) return;
            const media = [];
            for(let i = 0; i < results.length; i++){
                media[i] = await getMediaFileInfo(results[i]?.uri);
            }
            if(!cancelled) setAttachmentMedia(media.filter(Boolean));
        });

        return () => { cancelled = true };
    }, [JSON.stringify(comment?.attachments)]);

    const commentReactions = Object.entries(reactions)?.map(([emojiName, list]) => (
        <Reaction key={emojiName} emojiName={emojiName} people={list.map(r => r.Person)} onPress={() => {
            if(!canReact) return;
            onToggleReaction(comment, emojiName);
        }}/>
    ))

    const options = [];
    canEdit && options.push({name: "Edit Comment", icon: dropdownIcons.edit, onSelect: () => onEditComment?.(comment)});
    options.push({
        name: "Copy Comment",
        icon: {android: "content_copy", ios: "doc.on.doc", web: "copy-outline"},
        onSelect: () => {
            if(!comment?.text) return;
            Clipboard.setString(comment.text);
            impactLight();
        },
    });
    canDelete && options.push({name: "Delete Comment", destructive: true, icon: dropdownIcons.delete, onSelect: () => {
        showAlert({
            title: "Delete Comment",
            subtitle: "Are you sure you want to delete this comment? You will not be able to recover it.",
            isDestructive: true,
            confirmText: "Delete",
            onConfirm: () => {
                onDeleteComment?.(comment);
            }
        })
    }});

    return <View style={styles.commentItem} testID={`comment-item-${comment?.id}`}>
        <View style={styles.commentTimelineColumn}>
            {!isLast && <View style={styles.commentTimelineLine} />}
            <Pressable onPress={() => router.push("/people/" + comment?.author?.id)} style={styles.commentAvatarWrap}>
                <Avatar size={32} profile={comment?.author} disableBorder/>
            </Pressable>
        </View>
        <View style={styles.commentContent}>
            <View style={styles.commentHeaderRow}>
                <Text style={styles.commentAuthorName}>{comment?.author?.firstName} {comment?.author?.lastName}</Text>
                {isPostAuthor && <Chip colorVariant="primary" textStyle={{fontSize: 9}}>Author</Chip>}
                <RelativeTime time={comment?.commentedAt} style={styles.commentDate} />
                {options.length > 0 && <DropdownMenu testID={`comment-menu-${comment?.id}`} options={options} />}
            </View>
            <Text style={styles.commentText}>
                {renderTextWithMentions(comment?.text, router)}
            </Text>
            {attachmentMedia.length > 0 && (
                <View style={styles.commentAttachments}>
                    <MediaViewer media={attachmentMedia} />
                </View>
            )}
            <View style={styles.commentReactionsRow}>
                { canReact &&
                    <TouchableOpacity ref={reactionRef} testID={`comment-add-reaction-${comment?.id}`} onPress={() => setMessageForReaction({comment, reactionRef, contextMenuOpen: true})} style={styles.addReactionButton}>
                        <Unicon name="happy-outline" size={16} colorVariant="textSecondary"/>
                        <Unicon name="add-outline" size={16} colorVariant="textSecondary"/>
                    </TouchableOpacity>
                }
                {commentReactions}
            </View>
        </View>
    </View>
}

export default function PostComments() {

    /*** Theme & Dimensions ***/
    const { rt } = useUnistyles();
    const { postID, boardID } = useLocalSearchParams();
    const router = useRouter();
    const { isNative, isWeb } = usePlatform();
    const isFocused = useIsFocused();

    if(!postID){
        Toast.show({type: 'error', text2: "Post not found"})
        router.canGoBack() ? router.back() : router.replace("/");
    }

    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: post, isLoading } = Query.posts.byId({id: postID});
    const { data: comments, isFetchingNextPage, hasNextPage, fetchNextPage } = Query.comments.byPostId({postID});
    const { data: loggedInUser } = Query.people.loggedInUser()

    //Opening the post directly counts as a view (no dwell delay — that's feed-only)
    useEffect(() => {
        if(post?.id) recordPostView({postID: post.id});
    }, [post?.id]);

    //Track recently visited post
    useEffect(() => {
        if(postID && post?.title){
            saveToRecents({
                id: postID,
                url: `/connect/boards/${post?.boardID}/posts/${postID}`,
                title: post?.title,
                params: { postID, boardID: post?.boardID },
            });
        }
    }, [postID, post?.title]);

    //Pages fetched newest-first; flatten then reverse for oldest-to-newest display
    const flatComments = useMemo(() => (comments?.pages?.flatMap(page => page.data) || []).reverse(), [comments]);

    const permissions = useMemo(() => getBoardPermissions(post?.Board, loggedInUser), [post?.Board, loggedInUser]);
    const [adminBannerDismissed, setAdminBannerDismissed] = useState(false);
    const showAdminBanner = loggedInUser?.isAdmin && !permissions?.canComment && !adminBannerDismissed;

    const addCommentToQueryCache = (comment) => {
        queryClient.setQueryData(QueryKeys.comments.byPostId(postID), old => {
            if(!old) return old;
            if(old.pages.some(page => page.data.some(c => c.id == comment.id))) return old;
            const pages = [...old.pages];
            pages[0] = {...pages[0], data: [comment, ...pages[0].data]};
            return {...old, pages};
        })
    }

    const updateCommentInQueryCache = (comment) => {
        queryClient.setQueryData(QueryKeys.comments.byPostId(postID), old => {
            if(!old) return old;
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(c => c.id == comment.id ? {...c, ...comment} : c)}))}
        })
    }

    const removeCommentFromQueryCache = (commentID) => {
        queryClient.setQueryData(QueryKeys.comments.byPostId(postID), old => {
            if(!old) return old;
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.filter(c => c.id != commentID)}))}
        })
    }

    const editingMessage = useMessagesStore(state => state.editingMessage)
    const setEditingMessage = useMessagesStore(state => state.setEditingMessage)
    const editingComment = editingMessage?._editingContext == 'post-comment' && editingMessage?.postID == postID
        ? editingMessage
        : null

    const updatePostInQueryCache = (postID, updater) => {
        queryClient.setQueryData(QueryKeys.posts.byId(postID), old => old ? updater(old) : old)
        const boardID = postRef.current?.boardID
        if(!boardID) return
        queryClient.setQueryData(QueryKeys.posts.byBoardId(boardID), old => {
            if(!old) return old
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(o => o?.id == postID ? updater(o) : o)}))}
        })
        queryClient.setQueryData(QueryKeys.posts.myBoardsFeed(), old => {
            if(!old) return old
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(o => o?.id == postID ? updater(o) : o)}))}
        })
    }

    const addReactionMutation = useMutation({
        mutationFn: addPostReaction,
        onMutate: (data) => {
            if(!data) return
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions || []), {...data, Person: loggedInUserRef.current}]}))
        }
    })
    const removeReactionMutation = useMutation({
        mutationFn: removePostReaction,
        onMutate: (data) => {
            if(!data) return
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions?.filter(r => !(r.postID == data.postID && r.personID == data.personID)) || [])]}))
        }
    })

    const loggedInUserRef = useRef(loggedInUser);
    loggedInUserRef.current = loggedInUser;

    const postRef = useRef(post);
    postRef.current = post;

    const updateCommentReactionsInQueryCache = (commentID, updater) => {
        queryClient.setQueryData(QueryKeys.comments.byPostId(postID), old => {
            if(!old) return old;
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(c => c?.id == commentID ? {...c, Reactions: updater(c.Reactions || [])} : c)}))}
        })
    }

    const addCommentReactionMutation = useMutation({
        mutationFn: addCommentReaction,
        onMutate: (data) => {
            if(!data) return
            //Upsert semantics: drop any existing reaction by this person, then append
            updateCommentReactionsInQueryCache(data.commentID, reactions => [...reactions.filter(r => r.personID != data.personID), {...data, Person: loggedInUserRef.current}])
        }
    })
    const removeCommentReactionMutation = useMutation({
        mutationFn: removeCommentReaction,
        onMutate: (data) => {
            if(!data) return
            updateCommentReactionsInQueryCache(data.commentID, reactions => reactions.filter(r => !(r.commentID == data.commentID && r.personID == data.personID)))
        }
    })

    //Race-safe toggle (same as ChatBubble): tapping the user's existing reaction
    //removes it; anything else is a single upsert add — the backend
    //UNIQUE(personID, commentID) switches the reaction without a remove+add race.
    const updateCommentReaction = useCallback(async (comment, name) => {
        const currentUserReaction = comment?.Reactions?.find(r => (r.Person?.id ?? r.personID) == loggedInUserRef.current?.id)

        if(currentUserReaction?.reaction == name){
            await removeCommentReactionMutation.mutateAsync({commentID: comment.id, personID: loggedInUserRef.current?.id})
            return
        }

        await addCommentReactionMutation.mutateAsync({commentID: comment.id, reaction: name, personID: loggedInUserRef.current?.id})
    }, [addCommentReactionMutation, removeCommentReactionMutation])

    const listRef = useRef(null);

    //Scroll to the newest comment. Native: scrollToEnd. Web: scrollToOffset with a huge
    //value gets clamped to the real max scroll (index-based scrolling undershoots with
    //estimated item sizes).
    const scrollToBottom = useCallback(() => {
        if(isNative){
            listRef.current?.scrollToEnd?.({animated: true});
        } else {
            listRef.current?.scrollToOffset?.({offset: Number.MAX_SAFE_INTEGER, animated: true});
        }
    }, [isNative]);

    const updateReaction = async (post, name) => {
        const currentUserReaction = post?.Reactions?.find(r => r.Person?.id == loggedInUser?.id)

        if(currentUserReaction?.reaction == name){
            await removeReactionMutation.mutateAsync({postID: post.id, personID: loggedInUser?.id})
            return
        }

        if(currentUserReaction){
            await removeReactionMutation.mutateAsync({postID: post.id, personID: loggedInUser?.id})
        }

        await addReactionMutation.mutateAsync({postID: post.id, reaction: name, personID: loggedInUser?.id})
    }

    const createNotificationMutation = useMutation({mutationFn: createNotification});
    const createCommentMutation = useMutation({
        mutationFn: saveComment,
        onSuccess: async (data) => {
            addCommentToQueryCache({...data, author: loggedInUser})

            //Refresh the post/feed caches so its comment-count chip stays accurate.
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.byId(postID)});
            if(post?.boardID){
                queryClient.invalidateQueries({queryKey: QueryKeys.posts.byBoardId(post.boardID)});
            }
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});

            //Reveal the just-sent comment (otherwise it sits behind the sticky input).
            //Two nudges: after commit, then after late-loading content settles.
            requestAnimationFrame(() => scrollToBottom());
            setTimeout(() => scrollToBottom(), 250);

            const otherCommenterIDs = data.Post?.Comments?.map(c => c.author?.id).filter(id => id != loggedInUser?.id) || []

            if(loggedInUser?.id != post?.author?.id){
                otherCommenterIDs.push(post?.author?.id)
            }

            const uniqueCommenters = [...new Set(otherCommenterIDs.filter(Boolean))];

            //Client-side filter: exclude users who muted this board (server-side gate is the safety net)
            let mutedPersonIDs = [];
            try { mutedPersonIDs = await getMutedPersonIDsForBoard(post?.Board?.id); } catch { /* server-side gate is the safety net */ }
            const mutedSet = new Set(mutedPersonIDs);

            const notificationDetails = uniqueCommenters?.filter(id => !mutedSet.has(id)).map(id => ({
                type: "NEW_COMMENT",
                targetPersonID: id,
                message: data.text,
                path: `connect/boards/${post?.Board?.id}/posts/${post?.id}`,
                title: id == post?.author?.id
                        ? `${loggedInUser?.firstName} ${loggedInUser?.lastName} commented on your post in ${post?.Board?.name}.`
                        : `${loggedInUser?.firstName} ${loggedInUser?.lastName} also commented on a post in ${post?.Board?.name}.`,
                parameters: {
                    canComment: true,
                    boardID: boardID,
                    postID: post?.id,
                    commentID: data.id,
                    contextName: post?.Board?.name
                }
            }))

            if(notificationDetails?.length > 0){
                createNotificationMutation.mutate(notificationDetails)
            }
        }
    })

    const editCommentMutation = useMutation({
        mutationFn: saveComment,
        onMutate: ({details}) => {
            updateCommentInQueryCache(details);
        },
        onSuccess: (data) => {
            if(data?.id) updateCommentInQueryCache(data);
        },
    })

    const handleEditComment = useCallback((comment) => {
        setEditingMessage({...comment, _editingContext: 'post-comment'});
    }, [setEditingMessage])

    //Comment delete: board admin / org admin / comment author only (gated in
    //CommentItem via canDelete). Optimistically remove from cache, then refresh
    //the post so its comment-count chip stays accurate.
    const deleteCommentMutation = useMutation({
        mutationFn: deleteComment,
        onMutate: ({commentID}) => {
            removeCommentFromQueryCache(commentID);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.comments.byPostId(postID)});
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.byId(postID)});
            if(post?.boardID){
                queryClient.invalidateQueries({queryKey: QueryKeys.posts.byBoardId(post.boardID)});
            }
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});
        },
    })

    const handleDeleteComment = useCallback((comment) => {
        deleteCommentMutation.mutate({commentID: comment?.id});
    }, [deleteCommentMutation])


    //Any unread notification referencing this post clears when viewed
    useMarkNotificationsRead({ postID })

    useFocusEffect(useCallback(() => {
        queryClient.invalidateQueries({queryKey: QueryKeys.posts.byId(postID)});
        queryClient.invalidateQueries({queryKey: QueryKeys.comments.byPostId(postID)});
    }, [postID]));

    useEffect(() => {
        if(isNative || !isFocused) return; // Native gets updates via push notifications (see notificationHandler.js)

        //Catch up on anything missed while the screen was unfocused
        queryClient.invalidateQueries({queryKey: QueryKeys.comments.byPostId(postID)});

        let commentsChannel;
        let cancelled = false;

        const setRealtimeUpdates = async () => {
            await supabase.realtime.setAuth();
            if(cancelled) return;

            commentsChannel = supabase.channel(`Comments:${postID}`, {
                config: { private: true },
            })

            commentsChannel.on('broadcast', { event: 'INSERT' }, (broadcastMessage) => {
                const record = broadcastMessage?.payload?.record;
                if(!record || record.authorID == loggedInUserRef.current?.id) return;
                addCommentToQueryCache(record)
                //Refresh the post/feed caches so its comment-count chip stays accurate.
                queryClient.invalidateQueries({queryKey: QueryKeys.posts.byId(postID)});
                if(boardID){
                    queryClient.invalidateQueries({queryKey: QueryKeys.posts.byBoardId(boardID)});
                }
                queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});
            })
            .on('broadcast', { event: 'UPDATE' }, (broadcastMessage) => {
                if(broadcastMessage?.payload?.record) updateCommentInQueryCache(broadcastMessage?.payload?.record)
            })
            .subscribe()
        }

        setRealtimeUpdates();

        return () => {
            cancelled = true;
            if(commentsChannel) supabase.removeChannel(commentsChannel);
        }
    }, [postID, boardID, isNative, isFocused])


    //Shared value for the growing multiline input — updated by MessageInput's onLayout.
    //ChatScrollView uses it to extend the scrollable range so the last comment isn't
    //hidden under a taller input.
    const extraContentPadding = useSharedValue(0);

    //Stable renderScrollComponent — an inline arrow makes LegendList rebuild its scroll
    //container every render, remounting the header <Post> and reloading its attachments
    //(image flicker on every comment). Keep the reference stable.
    const renderScrollComponent = useCallback(
        (props) => <ChatScrollView {...props} extraContentPadding={extraContentPadding} />,
        [extraContentPadding]
    );

    const renderItem = useCallback(({item, index}) => (
        <CommentItem
            comment={item}
            isLast={index === flatComments.length - 1}
            isPostAuthor={item?.author?.id == post?.author?.id}
            canReact={!!permissions?.canReact}
            canEdit={item?.author?.id == loggedInUser?.id}
            canDelete={permissions?.isAdmin || loggedInUser?.isAdmin || item?.author?.id == loggedInUser?.id}
            onToggleReaction={updateCommentReaction}
            onEditComment={handleEditComment}
            onDeleteComment={handleDeleteComment}
        />
    ), [flatComments.length, post?.author?.id, permissions?.canReact, permissions?.isAdmin, loggedInUser, updateCommentReaction, handleEditComment, handleDeleteComment])

    //Memoized so the header (and its <Post> attachments) doesn't remount on every render.
    const listHeader = useMemo(() => (
        <View style={styles.listHeader}>
            <Post post={post} variant="detail" disableNavigation showBoardName onDelete={() => router.back()} />
            <View style={styles.commentsHeaderRow}>
                <Text style={styles.commentsHeader}>Comments</Text>
                <Chip colorVariant="textSecondary" textStyle={{fontSize: 12}}>{post?.Comments?.[0]?.count ?? 0}</Chip>
            </View>
        </View>
    ), [post, router])

    //Native modal header: just a close button on the left, no confirm. Replaces
    //the old Stack headerLeft X (which was slow to dismiss — the native nav header
    //bar added layout overhead on close). ModalHeader is lighter and lets us
    //control the close path directly.
    const nativeHeader = !isWeb && (
        <ModalHeader
            title={post?.title || "Post"}
            cancelText="Close"
            onCancel={() => {
                Keyboard.dismiss();
                router.back();
            }}
            goBackOnCancel={false}
        />
    );

    //Archived boards are read-dead — the post, comments, comment input, and
    //reaction UI are all replaced by the notice.
    const isArchived = !!post?.Board?.isArchived;

    const screenContent = isLoading
        ? <View style={styles.screen}>
            {nativeHeader}
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large"/>
            </View>
        </View>
        : isArchived
        ? <View style={styles.screen}>
            {nativeHeader}
            <View style={styles.archivedContainer} testID="post-archived-notice">
                <Text style={styles.archivedTitle}>This board has been archived</Text>
                <Text style={styles.archivedSubtext}>New posts, comments, and reactions are turned off.</Text>
            </View>
        </View>
        : <View style={styles.screen}>
            {nativeHeader}
            {showAdminBanner && (
                <AdminViewingBanner
                    message="You don't have access to this post. You're viewing as an administrator for transparency."
                    topInset={rt.insets.top + 58}
                    onDismiss={() => setAdminBannerDismissed(true)}
                />
            )}
            <View style={[styles.listContainer, showAdminBanner && {paddingTop: 0}]}>
                <LegendList
                    ref={listRef}
                    recycleItems={Platform.OS !== 'web'}
                    keyExtractor={(item) => item.id}
                    showsVerticalScrollIndicator={false}
                    estimatedItemSize={70}
                    data={flatComments}
                    renderItem={renderItem}
                    contentContainerStyle={[styles.listContent, !permissions?.canComment && {paddingBottom: rt.insets.bottom + 20}]}
                    contentInsetAdjustmentBehavior="automatic"
                    progressViewOffset={1000}
                    renderScrollComponent={renderScrollComponent}
                    refreshing={false}
                    onRefresh={() => {
                        queryClient.invalidateQueries({queryKey: QueryKeys.posts.byId(postID)})
                        queryClient.invalidateQueries({queryKey: QueryKeys.comments.byPostId(postID)})
                    }}
                    onStartReachedThreshold={0.5}
                    onStartReached={() => {
                        if(!isFetchingNextPage && hasNextPage) fetchNextPage()
                    }}
                    ListHeaderComponent={listHeader}
                    ListEmptyComponent={
                        <View style={styles.emptyContainer}>
                            <Unicon name="chatbubble-ellipses-outline" size={28} colorVariant="textSecondary" />
                            <Text style={styles.emptyText}>No comments yet</Text>
                            <Text style={styles.emptySubtext}>Be the first to share what you think!</Text>
                        </View>
                    }
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
                />
            </View>

            { (permissions?.canComment || editingComment) &&
                <KeyboardStickyView>
                    {permissions?.canComment && <MessageInput
                        detection={["mention"]}
                        placeholder='Add a comment...'
                        enableAttachments
                        attachmentDirectory={"commentAttachments/" + postID}
                        bottomInset={Platform.OS !== 'web' ? rt.insets.bottom : 20}
                        extraContentPadding={extraContentPadding}
                        onSendMessage={(text, attachment_keys) => {
                            createCommentMutation.mutate({details:{
                                postID: postID,
                                authorID: loggedInUser?.id,
                                text: text,
                                attachments: attachment_keys,
                            }})

                            Keyboard.dismiss();
                        }}
                    />}
                    {editingComment && <EditContainer
                        conversationID={postID}
                        placeholder="Edit comment..."
                        bottomInset={Platform.OS !== 'web' ? rt.insets.bottom : 20}
                        onEditMessage={(text) => {
                            editCommentMutation.mutate({details: {
                                id: editingComment.id,
                                postID: editingComment.postID,
                                authorID: editingComment.authorID ?? editingComment.author?.id,
                                text,
                            }});
                        }}
                    />}
                </KeyboardStickyView>
            }

            <ConnectedReactionMenu
                selectionType="post-or-comment"
                onReactionPress={(target, name, selection) => selection?.comment
                    ? updateCommentReaction(selection.comment, name)
                    : updateReaction(target, name)}
                canReply={false}
                canCopy={(selection) => !selection?.comment}
            />
        </View>;

    // On web, wrap in ModalWrapper for the drawer chrome (slide-in panel,
    // backdrop, header with Close button). On native, the Stack's modal
    // presentation + headerLeft close button handle it.
    if (isWeb) {
        return (
            <ModalWrapper title={post?.title || "Post"} noPadding width={700} cancelText="Close">
                {screenContent}
            </ModalWrapper>
        );
    }

    return screenContent;
}


const styles = StyleSheet.create(({colors, fonts, spacing}, rt) => ({
    screen: {
        flex: 1,
        backgroundColor: colors.background,
    },
    listContainer: {
        flex: 1,
        //  paddingTop: rt.insets.top + 44,
    },
    listContent: {
        // paddingBottom: Platform.OS === 'web' ? rt.insets.bottom + 80 : (rt.isPortrait ? 0 : 100),
        width: "100%",
        ...(Platform.OS === 'web' ? { maxWidth: 700, alignSelf: "center" } : {}),
    },
    listHeader: {
        paddingTop: rt.breakpoint === 'xs' ? spacing.xs : spacing.sm,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.background,
    },
    archivedContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
    },
    archivedTitle: {
        fontSize: 20,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    archivedSubtext: {
        fontSize: 14,
        color: colors.textSecondary,
        marginTop: 8,
        textAlign: "center",
    },
    //Comments
    commentsHeaderRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginHorizontal: rt.breakpoint === 'xs' ? 0 : 12,
        paddingHorizontal: rt.breakpoint === 'xs' ? 16 : 8,
        paddingTop: 22,
        paddingBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    commentsHeader: {
        fontSize: 13,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.7,
        fontFamily: fonts.bold,
    },
    commentItem: {
        flexDirection: "row",
        marginHorizontal: rt.breakpoint === 'xs' ? 0 : 12,
        paddingHorizontal: rt.breakpoint === 'xs' ? 16 : 8,
        backgroundColor: colors.background,
    },
    commentTimelineColumn: {
        alignItems: "center",
        width: 36,
    },
    commentAvatarWrap: {
        paddingTop: 12,
    },
    commentTimelineLine: {
        position: "absolute",
        top: 12 + 16,
        bottom: -16,
        width: 2,
        backgroundColor: colors.border || colors.surface,
        alignSelf: "center",
        zIndex: 0,
    },
    commentContent: {
        flex: 1,
        marginLeft: 14,
        paddingVertical: 12,
        marginBottom: 0,
    },
    commentHeaderRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginBottom: 4,
    },
    commentAuthorName: {
        fontSize: 13,
        color: colors.text,
        fontFamily: fonts.bold,
    },
    commentDate: {
        color: colors.textSecondary,
        fontSize: 11,
    },
    commentText: {
        fontSize: 14,
        color: colors.text,
        lineHeight: 19,
    },
    commentAttachments: {
        marginTop: 8,
        maxWidth: 280,
    },
    commentReactionsRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        marginTop: 4,
    },
    addReactionButton: {
        backgroundColor: colors.surfaceVariant,
        borderRadius: 100,
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 4,
        paddingHorizontal: 8,
        marginHorizontal: 2,
        marginVertical: 4,
        _web: { _hover: { backgroundColor: colors.hover } },
    },
    //Empty state
    emptyContainer: {
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 40,
        gap: 6,
    },
    emptyText: {
        color: colors.textSecondary,
        fontSize: 15,
        fontFamily: fonts.heading,
    },
    emptySubtext: {
        color: colors.textSecondary,
        fontSize: 12,
    },
}))
